import { Controller } from '@hotwired/stimulus'
import { attributeMap } from '../utils/dom'

const FIT_PADDING = 48
const OUTLIER_MIN_DISTANCE_KM = 25
const OUTLIER_IQR_FACTOR = 1.5
const MIN_DOMINANT_GROUP_SIZE = 3
const MIN_DOMINANT_GROUP_RATIO = 0.6

/**
 * Stimulus-контроллер карты каталога клубов.
 *
 * Отображает Google Map с маркерами всех клубов из каталога.
 * Строит viewport по актуальным маркерам, читает координаты клубов из
 * data-атрибутов элементов каталога, а у постраничного каталога — из данных
 * всей выдачи (событие catalog:data). При клике на маркер показывает баллун
 * с инфо о клубе.
 *
 * @extends {Controller}
 */
export default class CatalogMapController extends Controller {
  static targets = ['canvas']

  static values = {
    latitude: Number,
    longitude: Number,
    mapId: String,
    zoom: { type: Number, default: 12 },
    maxZoom: { type: Number, default: 14 },
    // Отсечение выбросов рассчитано на городской разброс координат. На каталоге
    // страны оно выкинуло бы из viewport целые регионы — половину Индонезии или
    // юг Чили, — потому что там «доминирующая группа» это просто самая
    // населённая часть страны. Выключается со стороны шаблона.
    cropOutliers: { type: Boolean, default: true },
    openBalloonAriaLabel: { type: String, default: 'Show club information' },
    closeBalloonAriaLabel: { type: String, default: 'Close' }
  }

  #map = null
  // Храним координаты рядом с маркером, чтобы не зависеть от представления
  // position внутри AdvancedMarkerElement при расчёте viewport.
  #markerById = new Map()
  // null — событие catalog:filter ещё не приходило, видны все маркеры
  #visibleIds = null
  // Данные всей выдачи постраничного каталога; null — маркеры строятся по карточкам в разметке
  #records = null
  #markerClass = null
  #onMapsReady = null
  #onFilterChange = null
  #onCatalogData = null
  #resizeObserver = null
  #fitIdleListener = null
  #rendering = false
  #connected = false

  connect() {
    this.#connected = true
    this.#onMapsReady = this.#render.bind(this)
    this.#onFilterChange = this.#syncMarkersToFilter.bind(this)
    this.#onCatalogData = this.#replaceRecords.bind(this)
    document.addEventListener('google-maps:maps-ready', this.#onMapsReady)
    document.addEventListener('google-maps:ready', this.#onMapsReady)
    document.addEventListener('catalog:filter', this.#onFilterChange)
    document.addEventListener('catalog:data', this.#onCatalogData)

    // Карта сначала может быть display:none, а также меняет ширину при своём
    // развороте. ResizeObserver позволяет делать fitBounds только после того,
    // как у контейнера действительно появились ненулевые размеры.
    this.#resizeObserver = new ResizeObserver(() => this.#containerResized())
    this.#resizeObserver.observe(this.element)

    if (window.google?.maps?.importLibrary) this.#render()
  }

  disconnect() {
    this.#connected = false
    document.removeEventListener('google-maps:maps-ready', this.#onMapsReady)
    document.removeEventListener('google-maps:ready', this.#onMapsReady)
    document.removeEventListener('catalog:filter', this.#onFilterChange)
    document.removeEventListener('catalog:data', this.#onCatalogData)
    this.#resizeObserver?.disconnect()
    this.#fitIdleListener?.remove()
    this.#clearMarkers()
    this.#map = null
    this.#visibleIds = null
    this.#records = null
  }

  async #render() {
    if (this.#map || this.#rendering || !this.hasCanvasTarget) return
    this.#rendering = true

    try {
      let Map
      let AdvancedMarkerElement

      try {
        ;({ Map } = await google.maps.importLibrary('maps'))
        ;({ AdvancedMarkerElement } = await google.maps.importLibrary('marker'))
      } catch (error) {
        console.error('Failed to load Google Maps library', error)
        return
      }

      // Пока грузилась библиотека, контроллер мог отключиться (Turbo-навигация) —
      // не создаём карту на отсоединённом canvas
      if (!this.#connected) return

      const center = {
        lat: this.latitudeValue,
        lng: this.longitudeValue
      }

      this.#map = new Map(this.canvasTarget, {
        center,
        zoom: this.zoomValue,
        mapId: this.mapIdValue,
        // Нативный fullscreen перекрывает окно целиком, а по макету карта
        // должна расширяться только внутри контейнера каталога.
        fullscreenControl: false,
        gestureHandling: 'cooperative',
        mapTypeControl: false,
        streetViewControl: false,
        cameraControl: false,
        zoomControl: true
      })

      this.#map.addListener('click', () => this.#closeBalloon())

      this.#markerClass = AdvancedMarkerElement
      this.#addClubMarkers()
    } finally {
      this.#rendering = false
    }
  }

  /**
   * Постраничный каталог прислал данные всей выдачи: маркеры, построенные по одной
   * странице карточек, заменяются маркерами всех клубов.
   *
   * @param {CustomEvent<{ records: Object.<string, string|null>[] }>} event
   */
  #replaceRecords(event) {
    this.#records = event.detail.records
    if (!this.#map) return

    this.#clearMarkers()
    this.#addClubMarkers()
  }

  #addClubMarkers() {
    const AdvancedMarkerElement = this.#markerClass
    const records =
      this.#records ??
      Array.from(
        document.querySelectorAll('[data-catalog-target="item"]'),
        attributeMap
      )

    records.forEach((item) => {
      const lat = parseFloat(item['data-latitude'])
      const lng = parseFloat(item['data-longitude'])
      if (
        !Number.isFinite(lat) ||
        !Number.isFinite(lng) ||
        lat < -90 ||
        lat > 90 ||
        lng < -180 ||
        lng > 180
      ) {
        return
      }

      const content = this.#buildMarkerContent(item)
      const position = { lat, lng }

      const marker = new AdvancedMarkerElement({
        map: this.#map,
        position,
        title: item['data-name'] || '',
        content
      })

      this.#markerById.set(String(item['data-id']), { marker, position })
    })

    this.#applyFilterToMarkers()
  }

  /**
   * Запоминает список ID из события catalog:filter и применяет его к маркерам.
   * Событие может прийти до готовности карты — фильтр сохраняется и применяется
   * после создания маркеров. Пагинация не учитывается — на карте видны все
   * прошедшие фильтр клубы.
   *
   * @param {CustomEvent<{ ids: string[] }>} event
   */
  #syncMarkersToFilter(event) {
    const visibleIds = new Set(event.detail.ids.map(String))
    const membershipChanged =
      this.#visibleIds === null ||
      visibleIds.size !== this.#visibleIds.size ||
      [...visibleIds].some((id) => !this.#visibleIds.has(id))

    this.#visibleIds = visibleIds
    // Сортировка и пагинация тоже публикуют snapshot, но не меняют состав
    // выдачи. Не сбрасываем выбранный пользователем viewport без причины.
    if (!membershipChanged) return

    this.#applyFilterToMarkers()
  }

  #applyFilterToMarkers() {
    if (!this.#map) return

    this.#markerById.forEach(({ marker }, id) => {
      const visible =
        this.#visibleIds === null || this.#visibleIds.has(String(id))
      marker.map = visible ? this.#map : null
    })

    // Фильтр меняет не только набор маркеров, но и географию выдачи, поэтому
    // старый viewport после фильтрации больше не является корректным.
    this.#fitVisibleMarkers()
  }

  /**
   * Пересчитывает viewport после показа карты или изменения ширины контейнера.
   * Google Maps получает resize до fitBounds, иначе скрытый canvas сохраняет
   * некорректную проекцию и центр.
   */
  #containerResized() {
    if (!this.#map || this.element.clientWidth === 0) return

    google.maps.event.trigger(this.#map, 'resize')
    this.#fitVisibleMarkers()
  }

  /** Строит viewport по маркерам, прошедшим текущую клиентскую фильтрацию. */
  #fitVisibleMarkers() {
    if (!this.#map || this.element.clientWidth === 0) return

    const positions = []
    this.#markerById.forEach(({ position }, id) => {
      if (this.#visibleIds === null || this.#visibleIds.has(String(id))) {
        positions.push(position)
      }
    })

    if (positions.length === 0) return

    const viewportPositions = this.#positionsWithoutOutliers(positions)

    this.#fitIdleListener?.remove()
    this.#fitIdleListener = null

    if (viewportPositions.length === 1) {
      this.#map.setCenter(viewportPositions[0])
      this.#map.setZoom(this.maxZoomValue)
      return
    }

    const bounds = new google.maps.LatLngBounds()
    viewportPositions.forEach((position) => bounds.extend(position))

    // Совпадающие или очень близкие точки иначе приближаются до уровня здания.
    // Ограничение применяется после fitBounds, когда Google вычислил zoom.
    this.#fitIdleListener = this.#map.addListener('idle', () => {
      this.#fitIdleListener?.remove()
      this.#fitIdleListener = null

      const zoom = this.#map?.getZoom()
      if (Number.isFinite(zoom) && zoom > this.maxZoomValue) {
        this.#map.setZoom(this.maxZoomValue)
      }
    })

    // fitBounds сам выбирает масштаб с учётом фактического размера canvas.
    this.#map.fitBounds(bounds, FIT_PADDING)
  }

  /**
   * Убирает из расчёта границ только далёкие одиночные точки.
   *
   * Центр берётся как медиана координат, а порог — по IQR расстояний до неё.
   * Минимальный радиус в 25 км не объявляет обычные городские окраины
   * выбросами. Результат принимается лишь при наличии доминирующей группы:
   * так два реальных кластера в разных районах не будут ошибочно обрезаны.
   * Сами маркеры не скрываются — выбросы исключаются только из fitBounds.
   *
   * @param {{ lat: number, lng: number }[]} positions
   * @returns {{ lat: number, lng: number }[]}
   */
  #positionsWithoutOutliers(positions) {
    if (!this.cropOutliersValue) return positions
    if (positions.length < MIN_DOMINANT_GROUP_SIZE + 1) return positions

    const center = {
      lat: this.#median(positions.map(({ lat }) => lat)),
      lng: this.#median(positions.map(({ lng }) => lng))
    }
    const distances = positions.map((position) =>
      this.#distanceInKm(center, position)
    )
    const sortedDistances = [...distances].sort((a, b) => a - b)
    const q1 = this.#percentile(sortedDistances, 0.25)
    const q3 = this.#percentile(sortedDistances, 0.75)
    const threshold = Math.max(
      OUTLIER_MIN_DISTANCE_KM,
      q3 + OUTLIER_IQR_FACTOR * (q3 - q1)
    )
    const inliers = positions.filter(
      (_, index) => distances[index] <= threshold
    )

    const hasDominantGroup =
      inliers.length >= MIN_DOMINANT_GROUP_SIZE &&
      inliers.length / positions.length >= MIN_DOMINANT_GROUP_RATIO

    return hasDominantGroup ? inliers : positions
  }

  #median(values) {
    return this.#percentile(
      [...values].sort((a, b) => a - b),
      0.5
    )
  }

  #percentile(sortedValues, percentile) {
    const index = (sortedValues.length - 1) * percentile
    const lowerIndex = Math.floor(index)
    const fraction = index - lowerIndex
    const lower = sortedValues[lowerIndex]
    const upper =
      sortedValues[Math.min(lowerIndex + 1, sortedValues.length - 1)]

    return lower + (upper - lower) * fraction
  }

  #distanceInKm(from, to) {
    const radians = (degrees) => (degrees * Math.PI) / 180
    const latDelta = radians(to.lat - from.lat)
    const lngDelta = radians(to.lng - from.lng)
    const fromLat = radians(from.lat)
    const toLat = radians(to.lat)
    const haversine =
      Math.sin(latDelta / 2) ** 2 +
      Math.cos(fromLat) * Math.cos(toLat) * Math.sin(lngDelta / 2) ** 2
    // Из-за погрешности float значение для почти противоположных точек может
    // на ничтожную долю выйти за [0, 1] и превратить расстояние в NaN.
    const normalizedHaversine = Math.min(1, Math.max(0, haversine))
    const angle = Math.atan2(
      Math.sqrt(normalizedHaversine),
      Math.sqrt(1 - normalizedHaversine)
    )

    return 6371 * 2 * angle
  }

  /**
   * Строит DOM-элемент маркера: пин с логотипом + скрытый баллун.
   *
   * @param {Object.<string, string|null>} item - data-атрибуты карточки
   * @returns {HTMLElement}
   */
  #buildMarkerContent(item) {
    const name = item['data-name']
    const pinImage = item['data-pin-image']
    const photoUrl = item['data-photo-url']
    const rating = item['data-rating']
    const reviewsCount = item['data-reviews-count']
    const clubUrl = item['data-club-url']
    const balloonTags = item['data-balloon-tags']
    const city = item['data-city']
    const country = item['data-country']

    const wrapper = document.createElement('div')
    wrapper.className = 'map-marker'

    // — Пин —
    const pin = document.createElement('button')
    pin.type = 'button'
    pin.className = 'map-marker__pin'
    pin.setAttribute(
      'aria-label',
      name
        ? `${this.openBalloonAriaLabelValue}: ${name}`
        : this.openBalloonAriaLabelValue
    )
    pin.setAttribute('aria-expanded', 'false')

    if (pinImage) {
      const img = document.createElement('img')
      img.src = pinImage
      img.width = 56
      img.height = 56
      img.alt = ''
      pin.appendChild(img)
    }

    pin.addEventListener('click', (e) => {
      e.stopPropagation()
      const isOpen = wrapper.classList.contains('map-marker--open')
      this.#closeBalloon()
      if (!isOpen) {
        wrapper.classList.add('map-marker--open')
        pin.setAttribute('aria-expanded', 'true')
      }
    })

    wrapper.appendChild(pin)

    // — Баллун —
    const balloon = document.createElement('div')
    balloon.className = 'map-marker__balloon'

    // Кнопка закрытия
    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.className = 'btn btn--s btn--close map-marker__close'
    closeBtn.setAttribute('aria-label', this.closeBalloonAriaLabelValue)
    closeBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" data-icon="cross"><use href="#icon-cross"></use></svg>'
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      this.#closeBalloon()
    })
    balloon.appendChild(closeBtn)

    // Фото клуба
    if (photoUrl) {
      const photoLink = document.createElement('a')
      photoLink.href = clubUrl || '#'
      photoLink.className = 'map-marker__image'
      const photo = document.createElement('img')
      photo.src = photoUrl
      photo.alt = name || ''
      photo.width = 260
      photo.height = 174
      photo.loading = 'lazy'
      photoLink.appendChild(photo)
      balloon.appendChild(photoLink)
    }

    // Тело баллуна
    const body = document.createElement('div')
    body.className = 'map-marker__body'

    // Рейтинг
    const ratingNum = parseFloat(rating)
    if (Number.isFinite(ratingNum) && ratingNum > 0) {
      const ratingTag = document.createElement('span')
      ratingTag.className = 'tag tag--orange tag--rate'
      ratingTag.textContent = ratingNum.toFixed(1)
      if (reviewsCount) {
        const sub = document.createElement('span')
        sub.textContent = ` (${reviewsCount})`
        ratingTag.appendChild(sub)
      }
      body.appendChild(ratingTag)
    }

    // Название
    const title = document.createElement('h3')
    title.className = 'map-marker__title'
    const titleLink = document.createElement('a')
    titleLink.href = clubUrl || '#'
    titleLink.className = 'map-marker__link'
    titleLink.textContent = name || ''
    title.appendChild(titleLink)
    body.appendChild(title)

    // Теги: тип корта + тип локации
    let tags = []
    try {
      tags = JSON.parse(balloonTags || '[]')
    } catch {
      // ignore
    }

    if (tags.length > 0) {
      const tagsList = document.createElement('ul')
      tagsList.className = 'map-marker__tags-list'
      tags.forEach((text) => {
        const li = document.createElement('li')
        li.className = 'map-marker__tag-item'
        const tag = document.createElement('span')
        tag.className = 'tag tag--gray'
        tag.textContent = text
        li.appendChild(tag)
        tagsList.appendChild(li)
      })
      body.appendChild(tagsList)
    }

    // Город, Страна
    const parts = [city, country].filter(Boolean)
    if (parts.length > 0) {
      const address = document.createElement('address')
      address.className = 'map-marker__address'
      address.textContent = parts.join(', ')
      body.appendChild(address)
    }

    balloon.appendChild(body)
    wrapper.appendChild(balloon)

    return wrapper
  }

  #closeBalloon() {
    this.#markerById.forEach(({ marker }) => {
      marker.content?.classList?.remove('map-marker--open')
      marker.content
        ?.querySelector('.map-marker__pin')
        ?.setAttribute('aria-expanded', 'false')
    })
  }

  #clearMarkers() {
    this.#markerById.forEach(({ marker }) => {
      marker.map = null
    })
    this.#markerById.clear()
  }
}
